import React, { useEffect, useMemo, useState } from 'react';
import {
  Code2,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { deleteFabricTemplate, updateFabricTemplate } from '../../api/fabricTemplate.api';
import { hydrateFabricTemplate } from './templates';
import { resolveFlyerCopy, sanitizeFlyerCopy } from './flyerCopy';
import { useFabricTemplates } from './useFabricTemplates';
import AddFabricTemplateModal from './AddFabricTemplateModal';
import FabricTemplateHtmlFrame from './FabricTemplateHtmlFrame';
import './fabricTemplates.css';

const LOCAL_COPY_PREFIX = 'fabricTemplateCopy:';

function localCopyKey(template) {
  const id = template?._id || template?.id || template?.builtInKey;
  return id ? `${LOCAL_COPY_PREFIX}${id}` : null;
}

function readLocalCopy(template) {
  const key = localCopyKey(template);
  if (!key || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalCopy(template, copy) {
  const key = localCopyKey(template);
  if (!key || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(copy));
  } catch {
    /* ignore quota errors */
  }
}

function TemplateThumb({ template }) {
  const { Component, thumbnail, designImage, templateKind, htmlContent, copy } = template;
  const img = thumbnail || designImage;

  if (img) {
    return (
      <div className="ftl-thumb-frame absolute inset-0">
        <img
          src={img}
          alt={template.name}
          className="ftl-thumb-image"
          loading="lazy"
        />
      </div>
    );
  }

  if (templateKind === 'html' && htmlContent) {
    return (
      <div className="absolute inset-0 flex flex-col bg-[#f3efe6]">
        <FabricTemplateHtmlFrame
          html={htmlContent}
          title={template.name}
          fill
          className="pointer-events-none opacity-90"
        />
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[0.55rem] font-semibold uppercase tracking-wider text-white">
          <Code2 size={10} />
          HTML
        </span>
      </div>
    );
  }

  if (Component) {
    return (
      <div className="ftl-flyer--thumb absolute inset-0 overflow-hidden">
        <Component copy={copy} />
      </div>
    );
  }

  if (templateKind === 'html') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#ebe4d8] text-[#5c564e]">
        <Code2 size={28} strokeWidth={1.5} />
        <span className="text-[0.65rem] font-semibold uppercase tracking-wider">HTML</span>
      </div>
    );
  }

  return <div className="absolute inset-0 bg-[#ebe4d8]" />;
}

function TemplatePreviewBody({
  template,
  draftCopy,
  onCopyChange,
  canEditCopy,
}) {
  const Component = template.Component;

  if (template.templateKind === 'html' && template.htmlContent) {
    return (
      <div className="overflow-hidden rounded-lg bg-[#121018]">
        <FabricTemplateHtmlFrame
          html={template.htmlContent}
          title={template.name}
          className="min-h-[60vh] h-[60vh] max-h-[70vh] rounded-lg"
        />
      </div>
    );
  }

  // Live React flyer — editable when built-in Component exists
  if (Component) {
    return (
      <div className={`relative overflow-hidden rounded-lg ${canEditCopy ? 'ftl-flyer--editable' : ''}`}>
        <Component
          copy={draftCopy}
          editable={canEditCopy}
          onCopyChange={canEditCopy ? onCopyChange : undefined}
        />
        {canEditCopy && (
          <p className="ftl-edit-hint">
            Click any text to edit. Use your browser&apos;s Print → Save as PDF for export.
          </p>
        )}
      </div>
    );
  }

  const flyerImage = template.thumbnail || template.thumbnailUrl;
  if (flyerImage) {
    return (
      <img
        src={flyerImage}
        alt={template.name}
        className="w-full rounded-lg object-contain max-h-[70vh] mx-auto"
      />
    );
  }

  return (
    <img
      src={template.designImage || template.designImageUrl}
      alt={template.name}
      className="w-full rounded-lg object-contain max-h-[70vh] mx-auto"
    />
  );
}

function PreviewModal({
  template,
  onClose,
  isAdmin,
  onDelete,
  deleting,
  onSaved,
  showNotification,
}) {
  const canEditCopy = Boolean(template.Component && template.builtInKey);
  const defaults = useMemo(
    () => (template.builtInKey ? resolveFlyerCopy(template.builtInKey, null) : null),
    [template.builtInKey]
  );
  const [draftCopy, setDraftCopy] = useState(() => {
    if (!template.builtInKey) return null;
    const local = readLocalCopy(template);
    return resolveFlyerCopy(template.builtInKey, local || template.copy);
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!template.builtInKey) {
      setDraftCopy(null);
      setDirty(false);
      return;
    }
    const local = readLocalCopy(template);
    setDraftCopy(resolveFlyerCopy(template.builtInKey, local || template.copy));
    setDirty(false);
    // Reset draft when opening a different template.
  }, [template.id, template._id, template.builtInKey]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleCopyChange = (next) => {
    setDraftCopy(next);
    setDirty(true);
  };

  const handleReset = () => {
    if (!defaults) return;
    setDraftCopy(defaults);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!canEditCopy) return;
    const cleaned = sanitizeFlyerCopy(template.builtInKey, draftCopy);
    if (!cleaned) return;

    // Always keep a local copy so edits survive reloads for this browser.
    writeLocalCopy(template, cleaned);

    if (!isAdmin) {
      setDirty(false);
      showNotification?.('Template text saved on this device', 'success');
      onSaved?.({ ...template, copy: cleaned });
      return;
    }

    const id = template._id || template.id;
    if (!id) {
      setDirty(false);
      showNotification?.('Template text saved on this device', 'success');
      onSaved?.({ ...template, copy: cleaned });
      return;
    }

    setSaving(true);
    try {
      const updated = await updateFabricTemplate(id, { copy: cleaned });
      const hydrated = hydrateFabricTemplate(updated);
      setDirty(false);
      showNotification?.('Template text saved', 'success');
      onSaved?.(hydrated || { ...template, copy: cleaned });
    } catch (err) {
      showNotification?.(err?.response?.data?.message || 'Failed to save template text', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ftl-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="ftl-modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={template.name}
      >
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-[0.65rem] uppercase tracking-[0.2em] text-white/45">{template.category}</p>
            <h2 className="ftl-serif text-lg sm:text-xl text-white truncate">{template.name}</h2>
          </div>
          <div className="flex items-center gap-1">
            {canEditCopy && (
              <>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 text-xs font-medium transition-colors disabled:opacity-50"
                  aria-label="Reset text to defaults"
                >
                  <RotateCcw size={14} />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#6b2c3e] text-white text-xs font-semibold hover:bg-[#7d3548] transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                  aria-label="Save template text"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save
                </button>
              </>
            )}
            {template.canManage && (
              <button
                type="button"
                onClick={() => onDelete?.(template)}
                disabled={deleting}
                className="p-2 rounded-lg text-red-300 hover:text-white hover:bg-red-500/30 transition-colors disabled:opacity-50"
                aria-label="Delete template"
              >
                {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-3 sm:p-5 bg-[#121018]">
          <TemplatePreviewBody
            template={template}
            draftCopy={draftCopy}
            onCopyChange={handleCopyChange}
            canEditCopy={canEditCopy}
          />
          <p className="mt-4 text-center text-sm text-white/45 font-light">{template.tagline}</p>
        </div>
      </div>
    </div>
  );
}

export default function FabricTemplateLibraryView({ setActiveView, onSupportClick, showNotification }) {
  const { templates, loading, refresh, setTemplates } = useFabricTemplates();
  const [selected, setSelected] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const isAdmin =
    typeof localStorage !== 'undefined' && localStorage.getItem('role')?.toLowerCase() === 'admin';

  const handleDelete = async (template) => {
    if (!template?._id && !template?.id) return;
    const id = template._id || template.id;
    if (!window.confirm(`Delete template “${template.name}”? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await deleteFabricTemplate(id);
      showNotification?.('Template deleted', 'success');
      if (selected?.id === template.id) setSelected(null);
      await refresh();
    } catch (err) {
      showNotification?.(err?.response?.data?.message || 'Failed to delete template', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaved = (hydrated) => {
    if (!hydrated) return;
    setSelected(hydrated);
    setTemplates((prev) =>
      prev.map((t) => (t.id === hydrated.id || t._id === hydrated._id ? { ...t, ...hydrated } : t))
    );
  };

  return (
    <div className="ftl-root ftl-page ftl-page-grain">
      <main className="relative z-[1] pt-6 sm:pt-10 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="flex justify-end mb-6 sm:mb-8">
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center justify-center gap-2 shrink-0 px-5 py-3 rounded-full bg-[#1a2744] text-white text-sm font-semibold hover:bg-[#243552] shadow-[0_10px_24px_-12px_rgba(26,39,68,0.55)] transition-colors"
            >
              <Plus size={16} />
              Add template
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#5c564e]">
              <Loader2 className="animate-spin mr-2" size={22} />
              Loading templates…
            </div>
          ) : templates.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#d4cbbd] bg-white/50 px-6 py-20 text-center">
              <Sparkles className="mx-auto mb-3 text-[#8a8378]" size={28} />
              <p className="ftl-serif text-xl text-[#1a2744]">No templates yet</p>
              <p className="text-sm text-[#5c564e] mt-2">Templates you add will appear here.</p>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1a2744] text-white text-sm font-semibold"
              >
                <Plus size={15} />
                Add your first template
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {templates.map((template, index) => (
                <article
                  key={template.id}
                  className="ftl-card-enter ftl-template-card group"
                  style={{ animationDelay: `${index * 45}ms` }}
                >
                  <button
                    type="button"
                    onClick={() => setSelected(template)}
                    className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6b2c3e]/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f3efe6] rounded-[0.9rem]"
                  >
                    <div className="ftl-template-thumb-wrap">
                      <TemplateThumb template={template} />
                      <span
                        className="ftl-template-badge"
                        style={{ background: template.accent || '#1a2744' }}
                      >
                        {template.category || 'Custom'}
                      </span>
                      <div className="ftl-template-thumb-fade" aria-hidden />
                    </div>
                  </button>
                  {template.canManage && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(template);
                      }}
                      disabled={deletingId === (template._id || template.id)}
                      className="ftl-delete-btn"
                      aria-label={`Delete ${template.name}`}
                    >
                      {deletingId === (template._id || template.id) ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      {selected && (
        <PreviewModal
          template={selected}
          onClose={() => setSelected(null)}
          isAdmin={isAdmin}
          onDelete={handleDelete}
          deleting={deletingId === (selected._id || selected.id)}
          onSaved={handleSaved}
          showNotification={showNotification}
        />
      )}

      <AddFabricTemplateModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        showNotification={showNotification}
        onCreated={async (created) => {
          await refresh();
          const hydrated = hydrateFabricTemplate(created);
          if (hydrated) setSelected(hydrated);
        }}
      />
    </div>
  );
}
